import React, {useContext, useState} from 'react';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import Link from '@mui/material/Link';
import Grid from "@mui/material/GridLegacy";
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import {useAuth} from "../../../hooks/useAuth";
import { useNavigate } from 'react-router-dom';
import * as Yup from "yup";
import {useForm} from "react-hook-form";
import {yupResolver} from "@hookform/resolvers/yup";
import {ToasterContext} from "../../shared/Toaster/ToasterContext";
import {NotificationType} from "../../../constants/global";
import {routeConfigs} from "../../../navigation/routeConfig";
import {CircularProgress, InputAdornment} from "@mui/material";
import IconButton from "@mui/material/IconButton";
import {Visibility, VisibilityOff} from "@mui/icons-material";

const Login: React.FunctionComponent = () => {
  const {addToast} = useContext(ToasterContext);
  const auth: any = useAuth();
  const navigate  = useNavigate();
  const [loading, setLoading] = useState<boolean>(false);
  const [newPasswordLoading, setNewPasswordLoading] = useState<boolean>(false);
  const [showPassword, setShowPassword] = React.useState(false);
  const [loginResult, setLoginResult] = useState<any>(null);

  const handleClickShowPassword = () => setShowPassword((show) => !show);

  const handleMouseDownPassword = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
  };

  const validationSchema = Yup.object().shape({
    email: Yup.string()
      .required('Email is Required'),
    password: Yup.string()
      .required('Password is required')
  });

  const newPasswordValidationSchema = Yup.object().shape({
    password: Yup.string()
      .required('Password is required')
      .min(8, 'Password is too short - should be 8 chars minimum.')
      .matches(/[a-zA-Z]/, 'Password can only contain Latin letters.'),
    confirmPassword: Yup.string()
      .required('Password is required')
      .oneOf([Yup.ref('password')], 'Your passwords do not match.'),
  });

  const { register, handleSubmit, formState: { errors } } = useForm({
    resolver: yupResolver(validationSchema)
  });

  const { register: newPasswordRegister, handleSubmit: handleNewPasswordSubmit, formState: { errors: newPasswordErrors } } = useForm({
    resolver: yupResolver(newPasswordValidationSchema)
  });
  const onSubmit = async (data: any) => {
    setLoading(true);
    const result = await auth.signIn(data.email, data.password);

    if (result?.message === 'NEW_PASSWORD_REQUIRED') {
      setLoading(false);
      setLoginResult(result.result);
    } else if (result.success) {
      setLoading(false);
      setLoginResult(null);
      navigate ('/');
    } else {
      const message = result.message === 'User is not confirmed.' ? 'An account with this email already exists and is currently unverified. Please go to the "Forgot Password" section to complete the verification process. Thank you.' : result.message
      setLoading(false);
      setLoginResult(null);
      addToast({ type: NotificationType.Error, title: message });
    }
  };

  const onNewPasswordSubmit = async (data: any) => {
    setNewPasswordLoading(true);
    const res = await auth.completeNewPassword(loginResult, data.password);

    if (res.success) {
      setNewPasswordLoading(false);
      addToast({ type: NotificationType.Success, title: 'Password has been successfully changed.'  });
      navigate ('/');
    } else {
      setNewPasswordLoading(false);
      addToast({ type: NotificationType.Error,  title: res.message });
    }
  };

  if (loginResult) {
    return (
      <>
        <Typography component="h1" variant="h5">
          Set New Password
        </Typography>
        <Box component="form" onSubmit={handleNewPasswordSubmit(onNewPasswordSubmit)} noValidate sx={{mt: 2}}>
          <Box sx={{mt: 1}}>
            <TextField
              margin="normal"
              fullWidth
              id="password"
              label="New Password"
              autoComplete="password"
              type='password'
              helperText={newPasswordErrors.password?.message?.toString()}
              {...newPasswordRegister('password')}
              error={!!newPasswordErrors.password}
            />
            <TextField
              margin="normal"
              fullWidth
              id="confirmPassword"
              label="Confirm Password"
              autoComplete="confirmPassword"
              type='password'
              helperText={newPasswordErrors.confirmPassword?.message?.toString()}
              {...newPasswordRegister('confirmPassword')}
              error={!!newPasswordErrors.confirmPassword}
            />
            <Button
              type="submit"
              fullWidth
              variant="contained"
              sx={{mt: 3, mb: 2}}
            >
              Set New Password
              { newPasswordLoading && <CircularProgress sx={{ml: 1}} size={14} color='inherit' />}
            </Button>
          </Box>
        </Box>
      </>
    );
  }

  return (
    <>
      <Typography component="h1" variant="h5">
        Sign in
      </Typography>
      <Box component="form" onSubmit={handleSubmit(onSubmit)} noValidate sx={{mt: 2}}>
        <TextField
          margin="normal"
          required
          fullWidth
          id="email"
          label="Email Address"
          autoComplete="email"
          {...register('email')}
          helperText={errors.email?.message?.toString()}
          error={!!errors.email}
          autoFocus
        />
        <TextField
          margin="normal"
          required
          fullWidth
          label="Password"
          type={showPassword ? 'text' : 'password'}
          id="password"
          {...register('password')}
          helperText={errors.password?.message?.toString()}
          error={!!errors.password}
          autoComplete="current-password"
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton
                  aria-label="toggle password visibility"
                  onClick={handleClickShowPassword}
                  onMouseDown={handleMouseDownPassword}
                  edge="end"
                >
                  {showPassword ? <VisibilityOff /> : <Visibility />}
                </IconButton>
              </InputAdornment>
            )
          }}
        />
        <Button
          type="submit"
          fullWidth
          variant="contained"
          sx={{mt: 3, mb: 2}}
        >
          Sign In
          { loading && <CircularProgress sx={{ml: 1}} size={14} color='inherit' />}
        </Button>
        <Grid container>
          <Grid item xs>
            <Link href={routeConfigs.forgotPassword.path} variant="body2">
              Forgot password?
            </Link>
          </Grid>
          <Grid item>
            Don't have an account?
            <Link href={routeConfigs.signUp.path} sx={{ml: 1}} variant="body2">
              Sign Up
            </Link>
          </Grid>
        </Grid>
      </Box>
    </>
  );
}

export default Login;